// libs
import React from 'react'
import { Box, Grid } from '@material-ui/core'
import TextField, {
  TextFieldProps
} from 'presentation/shared/components/TextField'
import { EmergencyRoomAttendanceModel } from 'domain/entities/emergency-room-attendance.model'
import { defaultGridProps } from '../Constants'
import Heading from 'presentation/shared/components/Heading'
import { ReactComponent as AlertIcon } from 'presentation/assets/icons/alert-triangle.svg'
import { BreathingDifficultyWrapper, PainAlertChip } from './styles'
import SupportText from 'presentation/shared/components/SupportText'

type PatientPainProps = {
  emergencyRoom?: EmergencyRoomAttendanceModel
}

export function PatientSymptoms({ emergencyRoom }: PatientPainProps) {
  const painIntensityInfo = emergencyRoom?.symptoms?.getPainIntensityInfo()

  const iconTextFieldProps: Partial<TextFieldProps> = {
    iconPosition: 'right',
    iconLocale: 'inside',
    iconMargin: '58px',
    iconDisabled: true
  }

  const getBooleanLabel = (value?: boolean): string => {
    if (value === false) return 'Não'
    if (value === true) return 'Sim'
    return '-'
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box mb={1} display="flex">
          <Heading color="primary">DORES</Heading>
          {painIntensityInfo?.hasAlert && (
            <PainAlertChip>
              <AlertIcon />
              <label>Alerta de dor!</label>
            </PainAlertChip>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} container spacing={4}>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dor de cabeça:"
            value={painIntensityInfo?.['head']?.label ?? '-'}
            icon={
              painIntensityInfo?.['head']?.isAlert && (
                <PainAlertChip noPadding>
                  <AlertIcon />
                </PainAlertChip>
              )
            }
            {...iconTextFieldProps}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dor no tórax:"
            value={painIntensityInfo?.['thorax']?.label ?? '-'}
            icon={
              painIntensityInfo?.['thorax']?.isAlert && (
                <PainAlertChip noPadding>
                  <AlertIcon />
                </PainAlertChip>
              )
            }
            {...iconTextFieldProps}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dor no abdômen:"
            value={painIntensityInfo?.['abdomen']?.label ?? '-'}
            icon={
              painIntensityInfo?.['abdomen']?.isAlert && (
                <PainAlertChip noPadding>
                  <AlertIcon />
                </PainAlertChip>
              )
            }
            {...iconTextFieldProps}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dor na lombar:"
            value={painIntensityInfo?.['low back']?.label ?? '-'}
            icon={
              painIntensityInfo?.['low back']?.isAlert && (
                <PainAlertChip noPadding>
                  <AlertIcon />
                </PainAlertChip>
              )
            }
            {...iconTextFieldProps}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Perda de força:"
            value={getBooleanLabel(emergencyRoom?.symptoms?.lossStrength)}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dificuldade para andar e falar:"
            value={getBooleanLabel(
              emergencyRoom?.symptoms?.difficultiesSpeakWalk
            )}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Sente confusão e/ou perda de memória:"
            value={getBooleanLabel(
              emergencyRoom?.symptoms?.confusionMemoryLoss
            )}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dor no peito com irradiação pro braço:"
            value={getBooleanLabel(emergencyRoom?.symptoms?.chestRadiatingArm)}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Sente dor abdominal:"
            value={getBooleanLabel(emergencyRoom?.symptoms?.abdominalPain)}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Tem hipertensão ou diabetes:"
            value={getBooleanLabel(
              emergencyRoom?.symptoms?.hypertensionDiabetes
            )}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Outro local de dor:"
            value={emergencyRoom?.symptoms?.otherPainLocation || '-'}
            disabled
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box mb={1}>
          <Heading color="primary">SINTOMAS</Heading>
        </Box>
      </Grid>
      <Grid item xs={12} container spacing={4}>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Febre:"
            value={emergencyRoom?.symptoms?.formatFeverOptions() || ''}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Temperatura:"
            value={emergencyRoom?.symptoms?.temperatureFever || '-'}
            disabled
          />
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Dificuldade respiratória:"
            value={getBooleanLabel(
              emergencyRoom?.symptoms?.breathingDifficulty
            )}
            disabled
          />
          <BreathingDifficultyWrapper>
            {emergencyRoom?.symptoms
              ?.formatBreathingDifficultyOptions()
              .map((option) => (
                <SupportText color="primary" key={option}>
                  {option}
                </SupportText>
              ))}
          </BreathingDifficultyWrapper>
        </Grid>
        <Grid item {...defaultGridProps}>
          <TextField
            label="Sente palpitação"
            value={getBooleanLabel(
              emergencyRoom?.symptoms?.isHeartPalpitations
            )}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Observações:"
            value={emergencyRoom?.symptoms?.details || '-'}
            disabled
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
